import {
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0xa000
const CHANNEL_START = 0x3030
const CHANNEL_SIZE = 16
const NAME_START = 0x7000
const NAME_SIZE = 11
const CHANNEL_COUNT = 250
const TYPE_ORDER = [0x02, 0x04, 0x06, 0x16, 0x17, 0x18, 0x19, 0x24, 0x25, 0x26]
const WRITE_TYPES = [
  [0x04, 0x1000],
  [0x06, 0x2000],
  [0x16, 0x3000],
  [0x24, 0x7000],
] as const

type GM30Spec = {
  id: string
  vendor: string
  model: string
  summary: string
}

export type GM30Profile = {
  protocol: 'radioddity-gm30'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 57600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 6
  memorySize: number
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly GM30Spec[] = [
  {
    id: 'radioddity-gm-30',
    vendor: 'Radioddity',
    model: 'GM-30',
    summary: 'Radioddity GM-30 GMRS handheld.',
  },
  {
    id: 'baofeng-gm-15pro',
    vendor: 'Baofeng',
    model: 'GM-15Pro',
    summary: 'Baofeng GM-15Pro GMRS handheld.',
  },
  {
    id: 'radioddity-mu-5',
    vendor: 'Radioddity',
    model: 'MU-5',
    summary: 'Radioddity MU-5 MURS handheld.',
  },
]

export const GM30_PROFILES: readonly GM30Profile[] = SPECS.map((spec) => ({
  protocol: 'radioddity-gm30',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '250 channels', '6-character names'],
  baudRate: 57600,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 6,
  memorySize: MEMORY_SIZE,
  powerLevels: ['Low', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeBbcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBbcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = length - 1; index >= 0; index -= 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  if (raw.every((value) => value === 0xff || value === 0)) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  const digital = (raw[1] & 0x80) !== 0
  const reverse = (raw[1] & 0x40) !== 0
  const value = decodeBbcd(Uint8Array.of(raw[1] & 0x3f, raw[0]))
  if (digital) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: value,
      polarity: reverse ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const value = encodeBbcd(Math.round(channel.ctcss * 10), 2)
    return Uint8Array.of(value[1], value[0])
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const encoded = encodeBbcd(channel.dtcs, 2)
    const result = Uint8Array.of(encoded[1], encoded[0])
    result[1] |= 0x80
    if (channel.dtcsPolarity === 'R') result[1] |= 0x40
    return result
  }
  return Uint8Array.of(0xff, 0xff)
}

function decodeName(image: Uint8Array, number: number): string {
  const start = NAME_START + (number - 1) * NAME_SIZE
  return String.fromCharCode(
    ...image.slice(start, start + 6),
  ).replace(/[\u0000\ufffd]/g, '').trim()
}

export function parseGM30Channel(
  image: Uint8Array,
  profile: GM30Profile,
  number: number,
): WebChannel {
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw.slice(0, 4).every((value) => value === 0xff)) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txAll = raw.slice(4, 8).every((value) => value === 0xff)
  const txFrequency = txAll ? null : decodeLbcd(raw.slice(4, 8)) ?? rxFrequency
  const txTone = decodeTone(raw.slice(11, 13))
  const rxTone = decodeTone(raw.slice(9, 11))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: decodeName(image, number),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[13] & 0x04 ? 'FM' : 'NFM',
    power: raw[13] & 0x02 ? 'High' : 'Low',
    skip: (raw[14] & 0x01) === 0,
  }
}

export function parseGM30Channels(
  image: Uint8Array,
  profile: GM30Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseGM30Channel(image, profile, index + 1),
  )
}

export function applyGM30Channel(
  image: Uint8Array,
  profile: GM30Profile,
  channel: WebChannel,
): void {
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  const nameStart = NAME_START + (channel.number - 1) * NAME_SIZE
  if (channel.empty) {
    raw.fill(0xff)
    raw[13] = 0x06
    raw[14] = 0x11
    raw[15] = 0
    image.fill(0, nameStart, nameStart + 6)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  raw.set(encodeTone(channel), 9)
  raw.set(encodeTone(channel), 11)
  raw[13] =
    (raw[13] & 0xf9) |
    (channel.mode === 'FM' ? 0x04 : 0) |
    (channel.power === 'High' ? 0x02 : 0)
  raw[14] =
    (raw[14] & 0xfe) |
    (channel.skip ? 0 : 0x01)
  raw[8] = 0
  image.fill(0, nameStart, nameStart + 6)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 6)
  for (let index = 0; index < name.length; index += 1) {
    image[nameStart + index] = name.charCodeAt(index)
  }
}

async function rawSend(
  transport: RadioTransport,
  data: Uint8Array,
  length: number,
): Promise<Uint8Array> {
  await transport.write(data)
  return transport.readExact(length, 3500)
}

async function ackAck(transport: RadioTransport): Promise<void> {
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error('The GM-30 rejected the transfer acknowledgement.')
  }
}

async function readCommand(
  transport: RadioTransport,
  command: Uint8Array,
  expected: number,
): Promise<Uint8Array> {
  const echoLength = command.length + 1
  const frame = new Uint8Array(1 + command.length)
  frame[0] = 0x52
  frame.set(command, 1)
  const response = await rawSend(transport, frame, expected + echoLength)
  if (response[0] !== 0x57 || response.length !== expected + echoLength) {
    throw new Error('The GM-30 returned an invalid read response.')
  }
  await ackAck(transport)
  return response.slice(echoLength)
}

async function enter(
  profile: GM30Profile,
  transport: RadioTransport,
): Promise<Record<number, number>> {
  await transport.discardInput()
  const searched = await rawSend(
    transport,
    new TextEncoder().encode('PSEARCH'),
    8,
  )
  if (searched[0] !== ACK || new TextDecoder().decode(searched.slice(1)) !== 'P13GMRS') {
    throw new Error(`The radio did not identify as ${profile.model}.`)
  }
  if ((await rawSend(transport, new TextEncoder().encode('PASSSTA'), 3)).some((value, index) => value !== [0x50, 0, 0][index])) {
    throw new Error('The GM-30 rejected the startup handshake.')
  }
  if ((await rawSend(transport, new TextEncoder().encode('SYSINFO'), 1))[0] !== ACK) {
    throw new Error('The GM-30 rejected the system information request.')
  }
  for (const [address, length] of [[0x00000a0d, 13], [0x00100a0d, 13], [0x00200a0d, 13], [0x0000000a, 11]] as const) {
    const command = new Uint8Array(5)
    command[0] = 0x56
    command[1] = (address >>> 24) & 0xff
    command[2] = (address >>> 16) & 0xff
    command[3] = (address >>> 8) & 0xff
    command[4] = address & 0xff
    await rawSend(transport, command, length)
    await ackAck(transport)
  }
  await transport.write(Uint8Array.of(0xff, 0xff, 0xff, 0xff, 0x0c))
  if ((await rawSend(transport, new TextEncoder().encode('P13GMRS'), 1))[0] !== ACK) {
    throw new Error('The GM-30 refused the second programming stage.')
  }
  await rawSend(transport, Uint8Array.of(0x02), 8)
  await ackAck(transport)
  const typeLocations: Record<number, number> = {}
  for (let index = 1; index < 16; index += 1) {
    const address = (index << 4) | 0x0f
    const response = await readCommand(
      transport,
      Uint8Array.of(0xff, address, 0x00, 0x01),
      1,
    )
    if (TYPE_ORDER.includes(response[0])) {
      typeLocations[response[0]] = index << 4
    }
  }
  return typeLocations
}

export async function readGM30RadioWithTransport(
  profile: GM30Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const locations = await enter(profile, transport)
  const image = new Uint8Array(MEMORY_SIZE)
  let offset = 0
  for (const type of TYPE_ORDER) {
    const location = locations[type]
    if (!location) throw new Error(`The GM-30 did not expose memory type 0x${type.toString(16)}.`)
    for (let index = 0; index < 16; index += 1) {
      for (const prefix of [0x00, 0x40, 0x80, 0xc0]) {
        const data = await readCommand(
          transport,
          Uint8Array.of(prefix, location + index, 0x00, 0x40),
          0x40,
        )
        image.set(data, offset)
        offset += 0x40
        onProgress?.({
          current: offset,
          total: MEMORY_SIZE,
          label: 'Reading Radioddity GM-30 memory',
        })
      }
    }
  }
  await transport.write(Uint8Array.of(ACK, ACK, 0x00))
  return { image, reportedModel: profile.model }
}

export async function writeGM30RadioWithTransport(
  profile: GM30Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than GM-30 memory.')
  }
  const locations = await enter(profile, transport)
  try {
    const totalWrites = WRITE_TYPES.length * 64
    let current = 0
    for (const [type, imageOffset] of WRITE_TYPES) {
      const location = locations[type]
      if (!location) throw new Error(`The GM-30 did not expose memory type 0x${type.toString(16)}.`)
      for (let index = 0; index < 16; index += 1) {
        for (const prefix of [0x00, 0x40, 0x80, 0xc0]) {
          const source = imageOffset + index * 0x100 + prefix
          const packet = new Uint8Array(5 + 0x40)
          packet.set([0x57, prefix, location + index, 0x00, 0x40])
          packet.set(image.slice(source, source + 0x40), 5)
          await transport.write(packet)
          if ((await transport.readExact(1, 3000))[0] !== ACK) {
            throw new Error(`The GM-30 rejected a write at 0x${source.toString(16)}.`)
          }
          current += 1
          onProgress?.({
            current,
            total: totalWrites,
            label: 'Writing Radioddity GM-30 memory',
          })
        }
      }
    }
  } finally {
    await transport.write(Uint8Array.of(ACK, ACK, 0x00))
  }
}
